import tracking from '@finn-no/analytics';
import pulse from '@finn-no/pulse-sdk';
import { PulseRecommendation } from './types';

declare var requestIdleCallback: any;

export default class Tracking {
    position: string;
    uuid: string;
    version: string;
    enabled: boolean;
    deviceType: string;
    trackInscreenIds: any[];
    trackInscreenTask?: Promise<void>;
    trackInscreenWithUrl: string[];
    trackingContext: {};

    readonly trackingDetailProperty;

    constructor({
        position = '',
        uuid = '',
        version = '',
        deviceType = null,
        trackingContext = null,
    }: {
        position?: string;
        uuid?: string;
        version?: string;
        deviceType?: string;
        trackingContext?: {};
    } = {}) {
        this.position = position;
        this.uuid = uuid;
        this.version = version;
        this.enabled = !!position;
        this.trackInscreenIds = [];
        this.deviceType = deviceType;
        this.trackingDetailProperty = 'personalized-ads';
        this.trackingContext = trackingContext;
    }

    sendIncreenTrackEvent(id: string, trackingUrls: string[]) {
        if (!this.enabled) {
            console.error('ignored sendIncreenTrackEvent', ...arguments);
            return;
        }

        this.trackInscreenIds.push({ id, trackingUrls });

        if (!this.trackInscreenTask) {
            this.trackInscreenTask = ('requestIdleCallback' in window
                ? requestIdleCallback
                : requestAnimationFrame)(() => {
                this.trackInscreenIds
                    .filter(
                        (item) =>
                            item.trackingUrls && item.trackingUrls.length > 0
                    )
                    .flatMap((i) => {
                        return i.trackingUrls;
                    })
                    .forEach((url) => {
                        this.trackWithUrl(url);
                    });

                const inscreenIdsFiltred = this.trackInscreenIds
                    .filter(
                        (item) =>
                            !item.trackingUrls || item.trackingUrls.length === 0
                    )
                    .map((i) => i.id);

                if (inscreenIdsFiltred.length > 0) {
                    tracking.trackEvent({
                        // @TODO provide an analyticsName
                        analyticsName: undefined,

                        ecType: 'USER',
                        ecCategory: 'recommendationEvents',
                        eventData: {
                            adIds: inscreenIdsFiltred.join(','),
                            version: this.version,
                            'recsys.uuid': this.uuid,
                            'recsys.position': `recommend.m-${this.position}.inscreen`,
                        },
                    });
                }

                this.trackInscreenIds.length = 0;
                delete this.trackInscreenTask;
            });
        }
    }

    trackEmpty() {
        if (!this.enabled) {
            console.error('ignored trackEmpty', ...arguments);
            return;
        }

        tracking.trackEvent({
            // @TODO provide an analyticsName
            analyticsName: undefined,

            ecType: 'USER',
            ecCategory: 'recommendationEvents',
            eventData: {
                version: this.version,
                'recsys.position': `recommend.${this.position}.empty`,
            },
        });
    }

    trackItemToPulseBasedOnContext(
        itemContext: {},
        keyWord: 'Click' | 'View',
        trackingContext: {}
    ): {} | undefined {
        if (!this.enabled) {
            console.error(`ignored ${keyWord} tracking to Pulse`, ...arguments);
            return;
        }
        if (!itemContext || !trackingContext) return;
        for (const [key] of Object.entries(itemContext)) {
            const event = itemContext[key].find(
                (event) => event['type'] === keyWord
            );
            const ctxValue = trackingContext[key];
            if (event && ctxValue) {
                event[key] = ctxValue;
                pulse.trackEvent(event, {disableAddClassifiedAdToItems: true});
            }
        }
    }

    trackClickWithPulse(item: PulseRecommendation) {
        this.trackItemToPulseBasedOnContext(item, 'Click', this.trackingContext);
    }

    trackInscreenWithPulse(item: PulseRecommendation) {
        this.trackItemToPulseBasedOnContext(item, 'View', this.trackingContext);
    }

    trackAdClick(
        adId: string,
        trackingUrlClick: string[],
        position?: string,
        type?: string
    ) {
        if (!this.enabled) {
            console.error('ignored trackAdClick', ...arguments);
            return;
        }

        if (trackingUrlClick) {
            trackingUrlClick.forEach((url) => {
                if (!url.includes('NoOfPageViews_Main')) {
                    this.trackWithUrl(url);
                }
            });

            if (type && position) {
                const adobeType: string =
                    type === 'mlt' ? 'more-like-this' : 'personalized-ads';
                const adobePos: string = position.split('_').shift();
                tracking.trackEventLater({
                    analyticsName: `recommendations.recommendation-clicked.${adobeType}`,
                    stringValue: adobePos,
                    context: {
                        finnkode: adId,
                    },
                });
            }
        } else {
            tracking.trackEventLater({
                // @TODO provide an analyticsName
                analyticsName: undefined,

                ecType: 'USER',
                ecCategory: 'recommendationEvents',
                eventData: {
                    version: this.version,
                    'recsys.uuid': this.uuid,
                    'recsys.position': `recommend.m-${this.position}.click`,
                },
                context: {
                    finnkode: adId,
                },
            });
        }
    }

    addDevice(url: string) {
        if (this.deviceType) {
            return `${url}&client.device=${this.deviceType}`;
        }
        return url;
    }

    trackWithUrl(url: string) {
        const urlWithDevice = this.addDevice(url);
        if (navigator.sendBeacon) {
            this.sendBeacon(urlWithDevice);
        } else {
            this.trackWithImage(urlWithDevice);
        }
    }

    sendBeacon(url: string) {
        navigator.sendBeacon(url);
    }

    trackWithImage(url: string) {
        const imgEl = document.createElement('img');
        imgEl.setAttribute('src', url);
        document.body.appendChild(imgEl);
        imgEl.style.display = 'none';
        imgEl.addEventListener('load', () => {
            document.body.removeChild(imgEl);
        });
    }

    static getInscreenUrl(ad: any) {
        if (ad.tracking === undefined || ad.tracking.ec === undefined)
            return undefined;
        return ad.tracking.ec.inScreen;
    }
    static getClickUrl(ad: any) {
        if (ad.tracking === undefined || ad.tracking.ec === undefined)
            return undefined;
        return ad.tracking.ec.click;
    }
}
